// @ts-nocheck
import {
  ControlBar,
  GridLayout,
  ParticipantTile,
  RoomAudioRenderer,
  useTracks,
  RoomContext,
} from "@livekit/components-react";
import { Room, Track } from "livekit-client";
import "@livekit/components-styles";
import { useContext, useEffect, useState } from "react";
import "./styles.scss";
import { sendMessage, subscribeToMessages } from "../../Lib/firebase";
import { MessageProps, RoomProps, StudentProfileProps } from "../../Lib/types";
import { PerformRequest, usePerformRequest } from "../../Lib/usePerformRequest";
import { Endpoints } from "../../Lib/endpoints";
import { NonPaginatedResponse } from "../../Lib/responses";
import {
  DefaultErrorNotification,
  DefaultSuccessNotification,
} from "../../Lib/methods";
import { useParams } from "react-router-dom";
import { AppContext } from "../Dashboard";
import { Text } from "@mantine/core";
import { Colors } from "../../Lib/data";
import { useDisclosure } from "@mantine/hooks";
import { Modal, Loader } from "@mantine/core";

const serverUrl = process.env.REACT_APP_LIVEKIT_SERVER_URL as string;

export default function OngoingLesson() {
  const context = useContext(AppContext);
  const { lessonID } = useParams();
  const [room] = useState(
    () =>
      new Room({
        adaptiveStream: true,
        dynacast: true,
      })
  );
  const [chatOpen, setChatOpen] = useState(false);
  const [messages, setMessages] = useState<MessageProps[]>([]);

  const {
    data: roomData,
    isLoading,
    error,
  } = usePerformRequest<NonPaginatedResponse<RoomProps>>({
    method: Endpoints.GetStartLesson(lessonID as string).method,
    route: Endpoints.GetStartLesson(lessonID as string).route,
  });

  useEffect(() => {
    if (!lessonID) return;
    if (roomData && roomData.data && roomData.data.token) {
      let mounted = true;
      const connect = async () => {
        if (mounted) {
          await room.connect(serverUrl, roomData.data.token);
        }
      };
      connect();
    }

    // return () => {
    //   mounted = false;
    //   room.disconnect();
    // };
  }, [room, serverUrl, roomData, lessonID]);

  useEffect(() => {
    if (!lessonID) return;
    // Subscribe to messages for this lesson
    const unsubscribe = subscribeToMessages(
      `live-chats/${lessonID}`,
      (msgs) => {
        setMessages(msgs.sort((a, b) => a.createdAt - b.createdAt));
      }
    );
    return unsubscribe;
  }, [lessonID]);

  return (
    <div className="ongoing-lesson-container">
      <RoomContext.Provider value={room}>
        <div data-lk-theme="default" style={{ height: "100vh" }}>
          <MyVideoConference />
          <RoomAudioRenderer />
          <ControlBar />
          <FloatingChatButton onClick={() => setChatOpen(true)} />
          {chatOpen && (
            <MeetingChat
              onClose={() => setChatOpen(false)}
              messages={messages}
              lessonID={lessonID as string}
            />
          )}
        </div>
      </RoomContext.Provider>
    </div>
  );
}

function MyVideoConference() {
  const context = useContext(AppContext);

  const tracks = useTracks(
    [
      { source: Track.Source.Camera, withPlaceholder: true },
      { source: Track.Source.ScreenShare, withPlaceholder: false },
    ],
    { onlySubscribed: false }
  );

  // Debug: Log all tracks to see what's available
  console.log("All tracks:", tracks);
  console.log(
    "Track sources:",
    tracks.map((t) => ({
      source: t.publication?.source,
      identity: t.participant.identity,
      isSubscribed: t.isSubscribed,
      isEnabled: t.publication?.isEnabled,
    }))
  );

  // Find any screen share track - try multiple detection methods
  const screenShareTrack = tracks.find(
    (t) =>
      t.publication &&
      t.publication.source === Track.Source.ScreenShare &&
      (t.isSubscribed || t.publication.isEnabled)
  );

  // Alternative: find any track with screen share source
  const anyScreenShare = tracks.find(
    (t) => t.publication?.source === Track.Source.ScreenShare
  );

  console.log("Screen share track found:", screenShareTrack);
  console.log("Any screen share found:", anyScreenShare);
  console.log(
    "Screen share track details:",
    screenShareTrack
      ? {
          source: screenShareTrack.publication?.source,
          identity: screenShareTrack.participant.identity,
          isSubscribed: screenShareTrack.isSubscribed,
          isEnabled: screenShareTrack.publication?.isEnabled,
        }
      : null
  );

  // Find teacher camera
  const teacherCamera = tracks.find(
    (t) =>
      t.publication &&
      t.publication.source === Track.Source.Camera &&
      t.participant.identity === "teacher"
  );

  // Get all camera tracks (excluding screen share)
  const cameraTracks = tracks.filter(
    (t) => t.publication?.source === Track.Source.Camera
  );

  // Use any screen share track we can find
  const activeScreenShare = screenShareTrack || anyScreenShare;

  console.log("Active screen share:", activeScreenShare);

  // If screen share is active, show theatre mode layout
  if (activeScreenShare) {
    console.log("Rendering theatre mode with screen share");
    return (
      <div
        style={{
          position: "relative",
          width: "100vw",
          height: "100vh",
          background: "#181c2f",
          display: "flex",
        }}
      >
        {/* Main screen share area (YouTube theatre mode) */}
        <div
          style={{
            flex: 1,
            height: "100vh",
            position: "relative",
          }}
        >
          <ParticipantTile
            trackRef={activeScreenShare}
            style={{
              width: "100%",
              height: "100%",
              objectFit: "contain",
              background: "#181c2f",
            }}
          />

          {/* Floating teacher camera overlay */}
          {teacherCamera && (
            <div
              style={{
                position: "absolute",
                right: 32,
                bottom: 32,
                width: 280,
                height: 160,
                zIndex: 2,
                background: "#22274a",
                borderRadius: 12,
                boxShadow: "0 8px 32px 0 rgba(102, 126, 234, 0.18)",
                overflow: "hidden",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                border: "2px solid #fff",
              }}
            >
              <ParticipantTile
                trackRef={teacherCamera}
                style={{ width: "100%", height: "100%" }}
              />
            </div>
          )}
        </div>

        {/* Participants sidebar */}
        <div
          style={{
            width: "300px",
            height: "100vh",
            background: "rgba(255, 255, 255, 0.95)",
            backdropFilter: "blur(20px)",
            borderLeft: "1px solid rgba(102, 126, 234, 0.1)",
            padding: "20px",
            overflowY: "auto",
          }}
        >
          <Text c={Colors.PurplePrimary} fw={600} size="lg" mb={16}>
            Participants ({cameraTracks.length})
          </Text>

          <div
            style={{ display: "flex", flexDirection: "column", gap: "12px" }}
          >
            {cameraTracks.map((track, index) => (
              <div
                key={index}
                style={{
                  background: "rgba(102, 126, 234, 0.05)",
                  borderRadius: "12px",
                  padding: "12px",
                  border: "1px solid rgba(102, 126, 234, 0.1)",
                }}
              >
                <Text size="sm" fw={500} mb={8} c={Colors.PurplePrimary}>
                  {track.participant.identity}
                </Text>
                <ParticipantTile
                  trackRef={track}
                  style={{
                    width: "100%",
                    height: "120px",
                    borderRadius: "8px",
                    overflow: "hidden",
                  }}
                />
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  console.log("Rendering normal grid layout");
  // Normal grid layout when no screen sharing
  return (
    <>
      <Text c={Colors.PurplePrimary} fw={500} ml={10}>
        Participants <b>({tracks.length})</b>
      </Text>
      <GridLayout
        style={{
          alignItems: "flex-start",
          justifyContent: "flex-start",
          gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))",
        }}
        tracks={tracks.filter(
          (t) => t.publication?.source !== Track.Source.ScreenShare
        )}
      >
        {/* @ts-ignore */}
        <ParticipantTile style={{ width: "190px", height: "190px" }} />
      </GridLayout>
    </>
  );
}

function FloatingChatButton({ onClick }: { onClick: () => void }) {
  return (
    <button className="floating-chat-btn" onClick={onClick}>
      <svg
        width="28"
        height="28"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
      </svg>
    </button>
  );
}

function MeetingChat({
  onClose,
  messages,
  lessonID,
}: {
  onClose: () => void;
  messages: MessageProps[];
  lessonID: string;
}) {
  const context = useContext(AppContext);
  const [input, setInput] = useState("");
  // TODO: Replace with real user info
  const currentUser = { id: context?.profile?.id as string, name: "You" };

  const handleSend = async () => {
    if (!input.trim()) return;
    const msg: MessageProps = {
      id: `${Date.now()}-${Math.random()}`,
      message: input,
      senderID: currentUser.id,
      createdAt: Date.now(),
      isTeacher: true,
      name: context?.profile?.name as string,
    };
    await sendMessage(`live-chats/${lessonID}`, msg);
    setInput("");
  };

  const [
    isStudentProfileModalOpen,
    { open: OpenStudentProfileModal, close: CloseStudentProfileModal },
  ] = useDisclosure(false);

  const [isLoadingStudentProfile, setLoadingStudentProfile] = useState(false);

  const [studentProfile, setStudentProfile] =
    useState<StudentProfileProps | null>(null);

  const GetStudentProfile = async (studentID: string) => {
    setLoadingStudentProfile(true);
    const r = await PerformRequest<NonPaginatedResponse<StudentProfileProps>>({
      method: Endpoints.GetSingleStudentProfile(studentID).method,
      route: Endpoints.GetSingleStudentProfile(studentID).route,
      callback: () => {
        setLoadingStudentProfile(false);
      },
    });
    setLoadingStudentProfile(false);

    if (r) {
      if (r.status === 200) {
        OpenStudentProfileModal();
        setStudentProfile(r.data.data);
      } else if (r.status === 404) {
        DefaultErrorNotification("Student not found");
      }
    }
    return null;
  };

  return (
    <div className="meeting-chat-panel">
      <div className="chat-header">
        <span>Meeting Chat</span>
        <button className="close-btn" onClick={onClose}>
          &times;
        </button>
      </div>
      <div className="chat-messages">
        {messages.map((msg) => (
          <div
            key={msg.id}
            className={
              "chat-message" + (msg.senderID === currentUser.id ? " self" : "")
            }
          >
            <div className="bubble">{msg.message}</div>
            <div
              className={`sender-name ${
                msg.senderID === currentUser.id ? "" : "student-msg-link"
              }`}
              onClick={() => {
                if (msg.senderID !== currentUser.id) {
                  // GET STUDENT PROFILE TO SHOW IN MODAL
                  GetStudentProfile(msg.senderID);
                }
              }}
            >
              {msg.senderID === currentUser.id ? currentUser.name : msg.name}
            </div>
            <div className="message-time">
              {new Date(msg.createdAt).toLocaleTimeString([], {
                hour: "2-digit",
                minute: "2-digit",
              })}
            </div>
          </div>
        ))}
      </div>
      <div className="chat-input-row">
        <input
          className="chat-input"
          placeholder="Type a message..."
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") handleSend();
          }}
        />
        <button className="send-btn" onClick={handleSend}>
          <svg
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <line x1="22" y1="2" x2="11" y2="13"></line>
            <polygon points="22 2 15 22 11 13 2 9 22 2"></polygon>
          </svg>
        </button>
      </div>

      {/* Student Profile Modal */}
      <Modal
        opened={isStudentProfileModalOpen}
        onClose={CloseStudentProfileModal}
        size="md"
        centered
        withCloseButton={false}
        classNames={{
          root: "student-profile-modal-root",
          content: "student-profile-modal-content",
          body: "student-profile-modal-body",
        }}
      >
        <div className="student-profile-modal">
          <div className="modal-header">
            <h3>Student Profile</h3>
            <button
              className="modal-close-btn"
              onClick={CloseStudentProfileModal}
            >
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <line x1="18" y1="6" x2="6" y2="18"></line>
                <line x1="6" y1="6" x2="18" y2="18"></line>
              </svg>
            </button>
          </div>

          {isLoadingStudentProfile ? (
            <div className="loading-container">
              <Loader size="lg" color={Colors.PurplePrimary} />
              <Text c={Colors.PurplePrimary} mt={16}>
                Loading student profile...
              </Text>
            </div>
          ) : studentProfile ? (
            <div className="profile-content">
              <div className="profile-avatar">
                <div className="avatar-placeholder">
                  {studentProfile.name.charAt(0).toUpperCase()}
                </div>
              </div>

              <div className="profile-info">
                <div className="info-section">
                  <label>Name</label>
                  <Text fw={600} size="lg">
                    {studentProfile.name}
                  </Text>
                </div>

                <div className="info-section">
                  <label>Email</label>
                  <Text size="md" c="dimmed">
                    {studentProfile.email}
                  </Text>
                </div>

                <div className="info-section">
                  <label>Status</label>
                  <div
                    className={`status-badge ${
                      studentProfile.isActive ? "active" : "inactive"
                    }`}
                  >
                    {studentProfile.isActive ? "Active" : "Inactive"}
                  </div>
                </div>

                <div className="info-section">
                  <label>Verification</label>
                  <div className="verification-status">
                    <div
                      className={`verification-item ${
                        studentProfile.isEmailVerified
                          ? "verified"
                          : "unverified"
                      }`}
                    >
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        {studentProfile.isEmailVerified ? (
                          <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                        ) : (
                          <circle cx="12" cy="12" r="10"></circle>
                        )}
                        {studentProfile.isEmailVerified && (
                          <polyline points="22,4 12,14.01 9,11.01"></polyline>
                        )}
                      </svg>
                      Email{" "}
                      {studentProfile.isEmailVerified
                        ? "Verified"
                        : "Unverified"}
                    </div>
                    <div
                      className={`verification-item ${
                        studentProfile.isPhoneVerified
                          ? "verified"
                          : "unverified"
                      }`}
                    >
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        {studentProfile.isPhoneVerified ? (
                          <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                        ) : (
                          <circle cx="12" cy="12" r="10"></circle>
                        )}
                        {studentProfile.isPhoneVerified && (
                          <polyline points="22,4 12,14.01 9,11.01"></polyline>
                        )}
                      </svg>
                      Phone{" "}
                      {studentProfile.isPhoneVerified
                        ? "Verified"
                        : "Unverified"}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="error-container">
              <Text c="red" ta="center">
                Failed to load student profile
              </Text>
            </div>
          )}
        </div>
      </Modal>
    </div>
  );
}
